import { forwardRef } from "react";
import {
  CircularProgress,
  Autocomplete as MUIAutoComplete,
  type AutocompleteProps as MUIAutocompleteProps,
} from "@mui/material";
import { Input, type InputProps } from "./Input";
import { mergeSx } from "#hooks/mergeSx";

export interface AutoCompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends Omit<
    MUIAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    "renderInput" | "ref"
  > {
  label?: string;
  helperText?: string;
  inputProps?: Omit<InputProps, "ref" | "label" | "helperText">;
}

function AutoCompleteInner<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(
  {
    label,
    helperText,
    inputProps,
    sx,
    ...rest
  }: AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: any
) {
  return (
    <MUIAutoComplete
      {...rest}
      sx={mergeSx(
        {
          width: "100%",
        },
        sx
      )}
      renderInput={(params) => {
        const {
          InputProps,
          InputLabelProps,
          inputProps: paramsInputProps,
          ...paramsRest
        } = params;
        return (
          <Input
            {...paramsRest}
            label={label}
            ref={ref}
            helperText={helperText}
            {...inputProps}
            slotProps={{
              ...inputProps?.slotProps,
              htmlInput: {
                ...paramsInputProps,
                ...inputProps?.inputProps,
                ...inputProps?.slotProps?.htmlInput,
              },
              input: {
                ...InputProps,
                ...inputProps?.InputProps,
                ...inputProps?.slotProps?.input,
                endAdornment: (
                  <>
                    {rest.loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {InputProps.endAdornment}
                    {inputProps?.InputProps?.endAdornment}
                    {/* @ts-expect-error error */}
                    {inputProps?.slotProps?.input?.endAdornment}
                  </>
                ),
              },
              // @ts-expect-error error
              inputLabel: {
                ...InputLabelProps,
                ...inputProps?.InputLabelProps,
                ...inputProps?.slotProps?.inputLabel,
              },
            }}
          />
        );
      }}
    />
  );
}

export const AutoComplete = forwardRef(AutoCompleteInner) as <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>(
  props: AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  }
) => ReturnType<typeof AutoCompleteInner>;
